import React, { useState, useMemo } from 'react';
import { Box, Tabs, Tab, Paper, Typography } from '@mui/material';
import { Bookmark, History, Label } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useDialer } from '../contexts/DialerContext';
import { usePermissions } from '../hooks/usePermissions';
import CallTrackingTab from '../components/tracking/CallTrackingTab';
import CallHistoryTab from '../components/tracking/CallHistoryTab';
import TagManagementPanel from '../components/tracking/TagManagementPanel';
import SkillSelectorModal from '../components/tracking/SkillSelectorModal';

/**
 * Página principal del módulo de llamadas del agente
 * Contiene tabs dinámicos según permisos: Seguimiento, Historial, Etiquetas
 */
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`agent-calls-tabpanel-${index}`}
      aria-labelledby={`agent-calls-tab-${index}`}
      style={{ height: '100%' }}
      {...other}
    >
      {value === index && <Box sx={{ height: '100%' }}>{children}</Box>}
    </div>
  );
}

const AgentCallsPage = () => {
  const { user } = useAuth();
  const { handleRedialFromAgentCalls } = useDialer();
  const { canAction } = usePermissions();
  const agentId = user?.username || user?.id;

  const [activeTab, setActiveTab] = useState(0);

  // Estados para modal de selección de skill
  const [skillSelectorOpen, setSkillSelectorOpen] = useState(false);
  const [skillSelectorData, setSkillSelectorData] = useState(null);

  // Construir tabs visibles según permisos
  const visibleTabs = useMemo(() => {
    const tabs = [];
    if (canAction('view_tracking')) {
      tabs.push({ key: 'tracking', label: 'En Seguimiento', icon: <Bookmark /> });
    }
    if (canAction('view_call_history')) {
      tabs.push({ key: 'history', label: 'Historial Completo', icon: <History /> });
    }
    if (canAction('manage_tags')) {
      tabs.push({ key: 'tags', label: 'Mis Etiquetas', icon: <Label /> });
    }
    return tabs;
  }, [canAction]);

  // Obtener key del tab activo
  const activeTabKey = visibleTabs[activeTab]?.key || null;

  // Manejar cambio de tab
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Manejar recuperación desde seguimiento (con datos del cliente)
  const handleRecoverFromTracking = async (trackingItem) => {
    console.log('[AGENT CALLS] Recuperar desde seguimiento:', trackingItem);

    if (!handleRedialFromAgentCalls) {
      console.error('[AGENT CALLS] handleRedialFromAgentCalls no está disponible');
      alert('Error: La funcionalidad de re-llamar no está disponible.');
      return;
    }

    // Llamar con número y datos del cliente del seguimiento
    handleRedialFromAgentCalls(trackingItem.phone_number, trackingItem.client_data);
  };

  // Manejar re-llamar desde historial (sin datos del cliente)
  const handleRedialFromHistory = async (historyItem) => {
    console.log('[AGENT CALLS] Re-llamar desde historial:', historyItem);

    if (!handleRedialFromAgentCalls) {
      console.error('[AGENT CALLS] handleRedialFromAgentCalls no está disponible');
      alert('Error: La funcionalidad de re-llamar no está disponible.');
      return;
    }

    // Llamar solo con número (sin datos de cliente)
    handleRedialFromAgentCalls(historyItem.phone_number, null);
  };

  // Manejar agendamiento de callback
  const handleScheduleCallback = async (trackingItem, scheduleData) => {
    console.log('[AGENT CALLS] Agendar callback:', trackingItem, scheduleData);
    // Manejado internamente por CallTrackingTab
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Paper elevation={1} sx={{ borderRadius: 0 }}>
        <Box sx={{ px: 3, pt: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Mis Llamadas
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Gestiona tus llamadas en seguimiento y consulta tu historial completo
          </Typography>

          {visibleTabs.length > 0 ? (
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="Tabs de llamadas"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  minHeight: 48,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 500
                }
              }}
            >
              {visibleTabs.map((tab, idx) => (
                <Tab
                  key={tab.key}
                  icon={tab.icon}
                  iconPosition="start"
                  label={tab.label}
                  id={`agent-calls-tab-${idx}`}
                  aria-controls={`agent-calls-tabpanel-${idx}`}
                />
              ))}
            </Tabs>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No tienes permisos para ver las secciones de este módulo.
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {activeTabKey === 'tracking' && (
          <Box sx={{ height: '100%' }}>
            <CallTrackingTab
              agentId={agentId}
              onRecoverCall={canAction('recover_call') ? handleRecoverFromTracking : undefined}
              onScheduleCall={canAction('schedule_callback') ? handleScheduleCallback : undefined}
            />
          </Box>
        )}

        {activeTabKey === 'history' && (
          <Box sx={{ height: '100%' }}>
            <CallHistoryTab
              agentId={agentId}
              onRedialNumber={canAction('redial_call') ? handleRedialFromHistory : undefined}
            />
          </Box>
        )}

        {activeTabKey === 'tags' && (
          <Box sx={{ height: '100%' }}>
            <TagManagementPanel />
          </Box>
        )}
      </Box>

      {/* Modal de selección de skill */}
      {skillSelectorData && (
        <SkillSelectorModal
          open={skillSelectorOpen}
          skills={skillSelectorData.skills || []}
          phoneNumber={skillSelectorData.phoneNumber}
          defaultSkill={skillSelectorData.defaultSkill}
          onSelect={(skill) => {
            console.log('[AGENT CALLS] Skill seleccionado:', skill);
            setSkillSelectorOpen(false);
            setSkillSelectorData(null);
            // TODO: Implementar lógica de llamada
          }}
          onCancel={() => {
            setSkillSelectorOpen(false);
            setSkillSelectorData(null);
          }}
        />
      )}
    </Box>
  );
};

export default AgentCallsPage;
